import { useCallback, useEffect, useRef } from 'react';
import { toast } from '@/hooks/use-toast';
import { useLocationSearch } from '@/hooks/useLocationSearch';
import { log } from '@/utils/logger';
import { getZoomLevelForLocationType } from '@/utils/mapUtils';
import { MapBounds } from '@/types/homepage';
import { useTranslation } from 'react-i18next';
import useHomePageStore from '@/store/useHomePageStore';

/**
 * Custom hook for search-related functionality in HomePage
 * Extracts search logic to reduce HomePage complexity
 */
export function useHomePageSearch(
  mapSectionRef: React.RefObject<HTMLDivElement>,
  setHasSearched: (value: boolean) => void
) {
  const { t } = useTranslation('public', { keyPrefix: 'homepage' });
  const { t: tc } = useTranslation('common');
  
  const {
    mapRef,
    mapStatus,
    setSearchQueryDebounced,
    handleMapSearch
  } = useHomePageStore();

  const { searchLocation } = useLocationSearch();
  const loggerRef = useRef(log.component('HomePageSearch'));
  const pendingSearchRef = useRef<string | null>(null);
  const pendingSearchOptionsRef = useRef<{ skipScroll?: boolean } | null>(null);
  // Signals to handleBoundsChange that the next moveend is from a programmatic search, not user interaction
  const isSearchMoveRef = useRef(false);

  // Tracks the active moveend handler so we can remove it if the component unmounts mid-animation
  const activeHandlerRef = useRef<{ map: maplibregl.Map; handler: () => void } | null>(null);

  // Store stable references for executeSearch
  const mapRefStable = useRef(mapRef);
  const searchLocationStable = useRef(searchLocation);
  const handleMapSearchStable = useRef(handleMapSearch);
  
  // Update refs when values change
  mapRefStable.current = mapRef;
  searchLocationStable.current = searchLocation;
  handleMapSearchStable.current = handleMapSearch;

  // Remove any pending moveend listener when the component unmounts mid-animation
  useEffect(() => {
    return () => {
      if (activeHandlerRef.current) {
        activeHandlerRef.current.map.off('moveend', activeHandlerRef.current.handler);
        activeHandlerRef.current = null;
      }
    };
  }, []);

  // Memoize the executeSearch function with stable dependencies
  const executeSearch = useCallback(async (query: string, directMapInstance?: maplibregl.Map, options?: { skipScroll?: boolean }) => {
    const searchOp = log.operation('executeSearch', 'HomePage');
    const trackingId = searchOp.start('Starting search execution', { query });
    
    const currentMap = directMapInstance || mapRefStable.current.current;
    loggerRef.current.debug('Search execution details', {
      query,
      mapReady: !!currentMap,
      currentCenter: currentMap?.getCenter(),
      currentZoom: currentMap?.getZoom(),
      timestamp: new Date().toISOString()
    });
    
    const searchResult = await searchLocationStable.current(query);
    loggerRef.current.debug('Geocoding result', {
      success: !!searchResult,
      coordinates: searchResult?.mapLibreCoordinates,
      displayName: searchResult?.displayName,
      locationType: searchResult?.locationType,
      timestamp: new Date().toISOString()
    });

    if (!searchResult) {
      loggerRef.current.warn('Geocoding failed for query', { query });
      searchOp.error('Geocoding failed', { query });
      toast({
        title: t('search.error'),
        description: t('search.locationNotFound'),
        variant: "destructive",
      });
      return;
    }
    
    const {
      mapLibreCoordinates,
      displayName: searchDisplayName,
      locationType,
      boundingbox
    } = searchResult;

    // Scroll to map section after paint so layout is stable (hero → map transition feels intentional)
    if (!options?.skipScroll && mapSectionRef.current) {
      const behavior = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      const el = mapSectionRef.current;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.scrollIntoView({ behavior, block: 'start' });
        });
      });
    }

    // Ensure we have a map reference
    if (!currentMap) {
      loggerRef.current.warn('Map not ready for search execution, storing for later', { query });
      searchOp.error('Map not ready', { query });
      pendingSearchRef.current = query;
      pendingSearchOptionsRef.current = options || null;
      return;
    }

    try {
      // Use fitBounds for all results that have a real bounding box; fall back to flyTo only
      // for point results (trivially small bbox).
      //
      // Some countries return a bbox contaminated by far-flung overseas territories:
      //   Norway: Bouvet Island at -54.654°S → latSpan = 135°  (Jan Mayen: west = -9.685°W)
      //   France: Réunion/Martinique/Guyana   → latSpan > 100°, lonSpan > 70°
      // Clamping south to (north - 26) and west to (east - 35) removes those outliers while
      // still keeping enough margin to show the country's main territory.
      const bboxNumbers = (boundingbox && boundingbox.length === 4 ? boundingbox : ['0', '0', '0', '0']).map(Number);
      const [south, north, west, east] = bboxNumbers;
      const latSpan = north - south;
      const lonSpan = east - west;
      const hasMeaningfulBBox =
        bboxNumbers.every(n => !isNaN(n)) &&
        (latSpan > 0.01 || lonSpan > 0.01);

      isSearchMoveRef.current = true;
      if (hasMeaningfulBBox) {
        const effectiveSouth = latSpan > 40 ? Math.max(south, north - 26) : south;
        const effectiveWest  = lonSpan > 35 ? Math.max(west,  east  - 35) : west;
        currentMap.fitBounds(
          [[effectiveWest, effectiveSouth], [east, north]],
          { padding: 60, duration: 2000, maxZoom: 14 }
        );
      } else {
        const zoomLevel = getZoomLevelForLocationType(locationType);
        currentMap.flyTo({ center: mapLibreCoordinates, zoom: zoomLevel, duration: 2000, essential: true });
      }

      // After camera movement completes, load businesses for the new viewport
      const handleFlyToComplete = () => {
        activeHandlerRef.current = null;
        isSearchMoveRef.current = false; // allow user-interaction bounds changes again
        loggerRef.current.info('FlyTo completed, triggering bounds change for new location');
        
        // Get the new bounds after flyTo
        const newBounds = currentMap.getBounds();
        const mapBounds: MapBounds = {
          northEast: {
            latitude: newBounds.getNorthEast().lat,
            longitude: newBounds.getNorthEast().lng
          },
          southWest: {
            latitude: newBounds.getSouthWest().lat,
            longitude: newBounds.getSouthWest().lng
          }
        };
        
        loggerRef.current.debug('Loading businesses for search destination bounds', mapBounds);
        
        // Trigger bounds change to load businesses for the new location
        handleMapSearchStable.current(mapBounds).then(businesses => {
          loggerRef.current.info('Loaded businesses for search destination', { count: businesses.length });
          searchOp.success('Search completed successfully', { 
            businessCount: businesses.length,
            destination: searchDisplayName 
          });
        }).catch(error => {
          loggerRef.current.error('Error loading businesses for search destination', error);
          searchOp.error('Failed to load businesses', error);
        });
        
      };

      // Remove any previous handler so concurrent searches don't double-fire
      if (activeHandlerRef.current) {
        activeHandlerRef.current.map.off('moveend', activeHandlerRef.current.handler);
      }
      activeHandlerRef.current = { map: currentMap, handler: handleFlyToComplete };
      currentMap.once('moveend', handleFlyToComplete);

    } catch (error) {
      loggerRef.current.error('Error during map flyTo', error);
      searchOp.error('FlyTo operation failed', error);
      toast({
        title: tc('error'),
        description: t('search.failedToMoveMap'),
        variant: "destructive",
      });
    }
  }, [toast, t, tc]); // STABLE - Only depend on stable functions

  // Handle search with debug logging and debouncing support
  const handleSearch = useCallback(async (query: string, coordinates?: [number, number], options?: { skipScroll?: boolean }) => {
    // Set hasSearched to true to show the map FIRST
    setHasSearched(true);
    
    // Use debounced search query update for performance
    setSearchQueryDebounced(query);
    
    // Check if map instance is available
    if (!mapRef.current) {
      loggerRef.current.warn('Map not ready yet, storing pending search', { 
        query,
        hasMapRef: !!mapRef.current,
        mapState: mapStatus.state
      });
      pendingSearchRef.current = query;
      pendingSearchOptionsRef.current = options || null;
      
      // Defer scroll until after React has painted the map section (avoids jarring layout shift)
      if (!options?.skipScroll && mapSectionRef.current) {
        const behavior = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        const el = mapSectionRef.current;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            el.scrollIntoView({ behavior, block: 'start' });
          });
        });
      }
      
      return;
    }
    
    try {
      // Execute the search immediately if map is ready
      await executeSearch(query, undefined, options);
    } catch (error) {
      loggerRef.current.error('Search execution failed', error);
      // Store the search for retry when map is ready
      pendingSearchRef.current = query;
      pendingSearchOptionsRef.current = options || null;
      
      // Show error toast
      toast({
        title: t('search.error'),
        description: t('search.failedToExecuteSearch'),
        variant: "destructive",
      });
    }
  }, [executeSearch, setHasSearched, setSearchQueryDebounced, mapRef, mapStatus, toast, t, mapSectionRef]);

  return {
    handleSearch,
    executeSearch,
    pendingSearchRef,
    pendingSearchOptionsRef,
    isSearchMoveRef
  };
} 